import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Card, CardBody, CardHeader } from 'reactstrap'
import { Breadcrumbs, H4 } from '../../AbstractElements'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import { useLayout } from '../../Hooks/useLayout'
import { Link } from 'react-router-dom'
import { getFile } from '../../lib/file'
import { getIdData } from '../../lib/profiles'

const ParentProfile = () => {
	const { user } = useAuth()
	const layout = useLayout()
	const [parentInfo, setParentInfo] = useState(null)
	const [avatar, setAvatar] = useState('')
	const [loading, setLoading] = useState(true)
	const [isDarkMode, setIsDarkMode] = useState(false)

	useEffect(() => {
		const fetchProfileData = async () => {
			if (!user) return

			try {
				const info = await getIdData(user.id)
				if (info && info.length > 0) {
					setParentInfo(info[0])

					// Fetch avatar if available
					if (info[0].avatar_url) {
						try {
							const avatarData = await getFile(info[0].avatar_url)
							setAvatar(avatarData.publicUrl)
						} catch (error) {
							console.error('Error fetching avatar:', error)
						}
					}
				}
			} catch (error) {
				console.error('Error fetching profile data:', error)
			} finally {
				setLoading(false)
			}
		}

		fetchProfileData()
	}, [user])

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['class'],
		})
		return () => observer.disconnect()
	}, [])

	if (!user) {
		return (
			<>
				<Breadcrumbs mainTitle="Parent Profile" parent="Profile" title="Parent Profile Details" />
				<Container fluid={true}>
					<Row>
						<Col sm="12">
							<Card style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}>
								<CardBody style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}>
									<div className="text-center">
										<div
											className="alert alert-warning"
											role="alert"
											style={{
												backgroundColor: isDarkMode ? 'rgba(255, 193, 7, 0.2)' : '#fff3cd',
												borderColor: isDarkMode ? 'rgba(255, 193, 7, 0.5)' : '#ffc107',
												color: isDarkMode ? '#ffd54f' : '#856404',
											}}
										>
											Please log in to view your profile.
										</div>
									</div>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</>
		)
	}

	if (loading) {
		return (
			<>
				<Breadcrumbs mainTitle="Parent Profile" parent="Profile" title="Parent Profile Details" />
				<Container fluid={true}>
					<Row>
						<Col sm="12">
							<Card style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}>
								<CardBody style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}>
									<div className="text-center">
										<div className="spinner-border text-primary" role="status">
											<span className="sr-only">Loading...</span>
										</div>
										<p
											className="mt-2"
											style={{
												color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
											}}
										>
											Loading...
										</p>
									</div>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</>
		)
	}

	return (
		<>
			<Breadcrumbs mainTitle="Parent Profile" parent="Profile" title="Parent Profile Details" />
			<Container fluid={true}>
				<Row>
					<Col sm="12">
						<Card style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}>
							<CardHeader
								style={{
									backgroundColor: isDarkMode ? '#2a2d35' : '#6c757d',
									borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6'}`,
								}}
							>
								<H4
									attrH4={{
										className: 'card-title mb-2',
										style: { color: '#fff' },
									}}
								>
									<i className="fa fa-users me-2"></i>
									Parent Profile Details
								</H4>
								<p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: 0 }}>
									Manage your personal information and account settings.
								</p>
							</CardHeader>
							<CardBody style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}>
								{/* Avatar Section */}
								<div
									className="course-group mb-4 p-4 border rounded"
									style={{
										backgroundColor: isDarkMode ? '#1d1e26' : '#f8f9fa',
										borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6',
									}}
								>
									<div className="d-flex align-items-center">
										<div className="position-relative">
											<img
												src={avatar || require('../../assets/images/user/user-dp.png')}
												alt="Profile Avatar"
												className="img-fluid rounded-circle border border-3 shadow"
												style={{
													width: '120px',
													height: '120px',
													objectFit: 'cover',
													transition: 'all 0.3s ease',
													borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : '#fff',
												}}
											/>
										</div>
										<div className="ms-4">
											<h4
												className="mb-2"
												style={{
													color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : '#6c757d',
												}}
											>
												Profile Picture
											</h4>
											<p
												className="mb-1"
												style={{
													color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#6c757d',
												}}
											>
												Your current profile picture
											</p>
											<small
												style={{
													color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#6c757d',
												}}
											>
												<i className="fa fa-info-circle me-1"></i>
												PNG or JPG, max 800x800 pixels
											</small>
										</div>
									</div>
								</div>

								{/* Parent Information */}
								<div className="settings-widget">
									<div className="settings-inner-blk p-0">
										<div className="checkout-form personal-address add-course-info">
											<div className="personal-info-head mb-4">
												<h4
													style={{
														color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : '#6c757d',
													}}
												>
													<i className="fa fa-user me-2"></i>
													Parent Details
												</h4>
												<p
													className="mb-0"
													style={{
														color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#6c757d',
													}}
												>
													Your personal information and account settings
												</p>
											</div>
											<div className="row">
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label
															className="form-control-label fw-bold mb-2"
															style={{
																color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#6c757d',
															}}
														>
															First Name
														</label>
														<div
															className="p-3 border rounded"
															style={{
																backgroundColor: isDarkMode ? '#1d1e26' : '#fff',
																borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6',
															}}
														>
															<p
																className="mb-0 fw-semibold"
																style={{
																	color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
																}}
															>
																{parentInfo?.name || 'Not specified'}
															</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label
															className="form-control-label fw-bold mb-2"
															style={{
																color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#6c757d',
															}}
														>
															Last Name
														</label>
														<div
															className="p-3 border rounded"
															style={{
																backgroundColor: isDarkMode ? '#1d1e26' : '#fff',
																borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6',
															}}
														>
															<p
																className="mb-0 fw-semibold"
																style={{
																	color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
																}}
															>
																{parentInfo?.surname || 'Not specified'}
															</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label
															className="form-control-label fw-bold mb-2"
															style={{
																color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#6c757d',
															}}
														>
															Phone
														</label>
														<div
															className="p-3 border rounded"
															style={{
																backgroundColor: isDarkMode ? '#1d1e26' : '#fff',
																borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6',
															}}
														>
															<p
																className="mb-0 fw-semibold"
																style={{
																	color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
																}}
															>
																{parentInfo?.phone || 'Not specified'}
															</p>
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="form-group mb-3">
														<label
															className="form-control-label fw-bold mb-2"
															style={{
																color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#6c757d',
															}}
														>
															Email
														</label>
														<div
															className="p-3 border rounded"
															style={{
																backgroundColor: isDarkMode ? '#1d1e26' : '#fff',
																borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6',
															}}
														>
															<p
																className="mb-0 fw-semibold"
																style={{
																	color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
																}}
															>
																{parentInfo?.email || 'Not specified'}
															</p>
														</div>
													</div>
												</div>
												<div className="col-12">
													<div className="update-profile d-flex flex-column flex-sm-row gap-3 justify-content-between mt-4">
														<Link
															to={`/parent/profile-edit/${layout}`}
															className="btn btn-secondary btn-lg px-4 py-2 text-white"
															style={{ minWidth: '140px' }}
														>
															<i className="fa fa-edit me-2"></i>
															Update Profile
														</Link>
														<Link
															to={`/parent/change-password/${layout}`}
															className="btn btn-warning btn-lg px-4 py-2 text-white"
															style={{ minWidth: '160px' }}
														>
															<i className="fa fa-key me-2"></i>
															Change Password
														</Link>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		</>
	)
}

export { ParentProfile }
